import type { RefObject } from 'preact';
import { type MutableRef, useCallback, useLayoutEffect, useRef, useState } from 'preact/hooks';

import uiConfig from 'Models/uiConfig/uiConfig';
import { animateLinear } from 'Utils/animatePromise';

import useIdleEffect from './idleEffect';

type UseCollapse = { isCollapsed: boolean; toggle: () => void };

type CollapsibleOptionsType = {
  disableCollapse?: boolean;
  initCollapsed?: boolean;
  openHeight?: number;
  prevExpandConditionRef?: MutableRef<boolean | undefined>;
  expandCondition?: boolean;
};

export function useCollapseFacet(
  rootRef: RefObject<HTMLElement | undefined>,
  disableCollapse: boolean | undefined,
  initCollapsed: boolean | undefined,
  hasSelection: boolean | undefined,
  hadSelectionRef: MutableRef<boolean>,
): UseCollapse {
  return useCollapsible(
    rootRef,
    '.facetbody',
    (!disableCollapse && uiConfig.facetsCollapsedDefault && !hasSelection) || initCollapsed || false,
    {
      disableCollapse,
      expandCondition: hasSelection,
      prevExpandConditionRef: hadSelectionRef,
    },
  );
}

const expandValue = 'expand';
const collapseValue = 'collapse';
const instantEnding = '-instant';
const instantExpandValue = `${expandValue}${instantEnding}`;

const checkIsCollapsed = (state: string) => state.startsWith(collapseValue);
const checkIsInstant = (state: string) => state.endsWith(instantEnding);
const checkIsInstantExpand = (state: string) => state === instantExpandValue;

const animatedAttr = 'data-cm-animated';
const collapsedClass = 'collapsed';
const smallBlockHeightThreshold = 150;
const smallBlockAnimationTime = 100;

export function useCollapsible(
  rootRef: RefObject<HTMLElement | undefined>,
  selector: string | null,
  initCollapsed: boolean,
  {
    disableCollapse,
    openHeight,
    expandCondition: expandNeeded,
    prevExpandConditionRef: prevExpandNeededRef = { current: undefined },
  }: CollapsibleOptionsType = {},
): UseCollapse {
  const [state, setState] = useState(`${initCollapsed ? collapseValue : expandValue}${instantEnding}`);

  const isInstant = checkIsInstant(state);
  const collapseNeeded = checkIsCollapsed(state);

  const animElemRef = useRef<HTMLElement>();
  const cancelAnimation = useRef<() => void>();

  useLayoutEffect(function initializeAnimatedElement() {
    if (disableCollapse) {
      return;
    }
    const root = rootRef.current;
    const elem = root?.querySelector(selector || '.cm_collapsible-elem');
    if (elem instanceof HTMLElement) {
      if (elem !== animElemRef.current) {
        animElemRef.current = elem;
        elem.style.position = 'relative';
      }
      if (isInstant) {
        if (collapseNeeded) {
          elem.style.height = '0';
          elem.style.overflow = 'hidden';
          elem.classList.add(collapsedClass);
        } else {
          elem.style.height = openHeight ? `${openHeight}` : '';
        }
      }
    } else if (root && selector) {
      console.warn(`animElem with "${selector}" selector for useCollapsible is not an HTMLElement: ${elem}`);
    }
  });

  useLayoutEffect(
    function expandInstantly() {
      if (collapseNeeded && !prevExpandNeededRef.current && expandNeeded) {
        setState(instantExpandValue);
      }
      prevExpandNeededRef.current = expandNeeded;
    },
    [collapseNeeded, expandNeeded, prevExpandNeededRef],
  );

  useIdleEffect(
    function handleAnimation() {
      if (disableCollapse) {
        return;
      }
      const animElem = animElemRef.current;
      if (!animElem || hasAnimatedChild(animElem)) {
        return;
      }

      cancelAnimation.current?.();
      if (!collapseNeeded) {
        animElem.classList.remove(collapsedClass);
      }

      const { height, maxHeight } = getHeights(animElem);
      const newHeight = (!collapseNeeded && (openHeight || maxHeight)) || 0;
      if (height === newHeight) {
        return;
      }
      const animationTime = getAnimationTime(height, newHeight);

      animElem.style.overflow = 'hidden';
      animElem.setAttribute(animatedAttr, '');

      const { promise, cancel } = animateLinear(
        height,
        newHeight,
        (h: number) => (animElem.style.height = `${h}px`),
        animationTime,
      );
      cancelAnimation.current = cancel;

      promise.then(() => {
        if (!collapseNeeded) {
          animElem.style.overflow = openHeight ? 'auto' : '';
          animElem.style.height = openHeight ? `${openHeight}` : '';

          if (!checkIsInstantExpand(state)) {
            // TODO: should be implemented via React functions similar to "jQueryUIs :focusable"

            // Do not steal focus if the user already focused something inside animElem during the animation.
            if (!animElem.contains(document.activeElement)) {
              animElem.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
            }
          }
        } else {
          animElem.classList.add(collapsedClass);
        }
        animElem.removeAttribute(animatedAttr);
      });
    },
    [collapseNeeded, disableCollapse, openHeight, state],
    { timeout: 500 },
  );

  const toggle = useCallback(
    () => setState((prevState) => (checkIsCollapsed(prevState) ? expandValue : collapseValue)),
    [],
  );
  return { isCollapsed: collapseNeeded, toggle };
}

function hasAnimatedChild(elem: HTMLElement): boolean {
  return !!(elem && elem.querySelector(`[${animatedAttr}]`));
}

function getHeights(elem: HTMLElement): { height: number; maxHeight: number } {
  const children = [...elem.children] as HTMLElement[];

  const visibleChildren = children.filter((child) => {
    return window.getComputedStyle(child).display !== 'none' && child.offsetHeight > 0;
  });

  const { offsetTop = 0, offsetHeight = 0 } = visibleChildren.pop() || {};

  return {
    height: elem.offsetHeight,
    maxHeight: offsetTop + offsetHeight,
  };
}

function getAnimationTime(fromHeight: number, toHeight: number): number {
  const baseTime = uiConfig.collapsibleAnimationTime;
  if (baseTime <= 0) {
    return 0;
  }

  const distance = Math.abs(toHeight - fromHeight);
  if (distance < smallBlockHeightThreshold) {
    return Math.min(baseTime, smallBlockAnimationTime);
  }

  return baseTime;
}
